import {
  GetFhirQuestionnaireByIdQuery,
  GetFhirQuestionnaireResponseQuery,
} from '../graphql/generated/remote-schema-hasura';
import { convertHealthQuestionnaireResponsesFromFhirToHasura } from './functions';
import {
  IMedication,
  QuestionnaireView,
  Response,
} from '../app/my-patients/pages/patient/tabs/questionnaire/useGetLastQuestionnaireByPatientCodexId';
import { ZRTMedication } from '../app/manage-test-kits/components/ZrtQuestionnaire/questionnaireResultComponents/Medication';
import {
  DynamicQuestionnaireDependencies,
  GetQuestionnaireOutput,
  HasuraAnswerItem,
} from '../graphql/generated/hasura';
import { findOtherFieldDependency } from '../components/stepper/field-components/QuestionnaireField';
import { Question } from '../components/questionnaire/hooks/useQuestionnaire';

interface Answer {
  questionID: number;
  response: string | Response;
}

export const ZRT_MEDICATION_FIELD_BY_QUESTION_ID = {
  171: 'displayName',
  173: 'dosage',
  174: 'dosageUnit',
  175: 'frequency',
  177: 'frequencyUnit',
  172: 'deliveryMethod',
  84: 'notes',
  180: 'notes',
} as const;

export function mapZRTMedicationAnswersToMedication(
  answers: { questionID: number; response: string }[],
): ZRTMedication {
  return answers.reduce<ZRTMedication>((acc, { questionID, response }) => {
    const field =
      ZRT_MEDICATION_FIELD_BY_QUESTION_ID[
        questionID as keyof typeof ZRT_MEDICATION_FIELD_BY_QUESTION_ID
      ];

    if (!field) return acc;

    acc[field] = response;
    return acc;
  }, {});
}

export function buildZRTMedications(
  questionnaireResponse: QuestionnaireView[],
): ZRTMedication[] {
  if (!questionnaireResponse?.length) return [];

  return questionnaireResponse
    .flatMap((item) => item.answer ?? [])
    .map((medicationItem) => {
      return mapZRTMedicationAnswersToMedication(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        medicationItem?.answers ||
          ([] as { questionID: number; response: string }[]),
      );
    });
}

export const convertQuestionnaireResponsesFromFhirToView = (
  responseData?: GetFhirQuestionnaireResponseQuery | null,
  questionData?: GetFhirQuestionnaireByIdQuery | null,
): QuestionnaireView[][] => {
  const rawResponses =
    responseData?.getFHIRQuestionnaireResponse.questionnaire_response.responses;
  const rawQuestions =
    questionData?.getFHIRQuestionnaireByCodexID
      .questionnaires_questionnaire_questions;

  const allDeps = questionData?.getFHIRQuestionnaireByCodexID.data.flatMap(
    (section) => section.dependencies || [],
  );

  if (!rawResponses?.length || !rawQuestions?.length) return [];

  const titleById = new Map(
    rawQuestions.map(({ questionnaire_questions_question }) => [
      questionnaire_questions_question.id.toString(),
      questionnaire_questions_question.text,
    ]),
  );

  const answerById = new Map(
    rawResponses
      .map((answer) => {
        if (!(answer as HasuraAnswerItem).questionID) return null;

        return [(answer as HasuraAnswerItem)?.questionID?.toString(), answer];
      })
      .filter(Boolean) as [string, HasuraAnswerItem][],
  );

  const {
    questionnaire_responses: [{ responses }],
  } = convertHealthQuestionnaireResponsesFromFhirToHasura(
    responseData as GetFhirQuestionnaireResponseQuery,
  );

  const dataQuestionnaire = (responses as Array<Record<string, string>>).reduce<
    Record<string, QuestionnaireView[]>
  >((sections, { currentIndexOfSection, questionID, response }) => {
    const title = titleById.get(questionID.toString()) ?? '';
    const question = { id: questionID } as Question;
    const compositeInfo = findOtherFieldDependency(question, allDeps || []);

    const dependentAnswers = compositeInfo
      .map((dep) => {
        const depAnswer = answerById.get(dep.dependentFieldId);

        return {
          questionID: dep.dependentFieldId,
          title: titleById.get(dep.dependentFieldId) ?? '',
          triggerParentValues: dep.triggerValues,
          answer:
            depAnswer?.stringResponse ||
            depAnswer?.stringArrayResponse ||
            depAnswer?.hasuraItemArrayResponse ||
            depAnswer?.hasuraMedicationItemResponse,
        } as QuestionnaireView;
      })
      .filter(Boolean);

    (sections[currentIndexOfSection] ??= []).push({
      title,
      questionID: questionID,
      answer: response,
      dependentAnswers,
    });

    return sections;
  }, {});

  return Object.values(dataQuestionnaire);
};

export const mergeMedicationResponses = (
  prev: string | Response,
  next: Response,
): string | Response =>
  typeof prev === 'string' ? next : `${prev.value} ${next.value}`;

export const convertMedications = (
  medications?: QuestionnaireView[],
  questionData?: Record<string, string>,
): IMedication[][] => {
  if (!medications?.length || !questionData) return [];

  return (medications[0].answer as { answers: Answer[] }[]).map(
    ({ answers }) => {
      const byQuestion = new Map<number, IMedication>();

      for (const { questionID, response } of answers) {
        const title = questionData[questionID.toString()] ?? '';
        const existing = byQuestion.get(questionID);

        byQuestion.set(questionID, {
          title,
          questionID,
          response: existing
            ? mergeMedicationResponses(existing.response, response as Response)
            : response,
        });
      }

      return [...byQuestion.values()];
    },
  );
};

export const getEnabledDependencyIdsAsNumber = (
  deps?: DynamicQuestionnaireDependencies[] | null,
): number[] => {
  if (!deps) return [];
  const set = new Set<number>();

  deps.forEach((d) => {
    (d.then?.enabled ?? []).forEach((id) => set.add(Number(id)));
  });

  return Array.from(set);
};

type SectionIdsResult = {
  sectionIndex: number;
  sectionName: string | null;
  fieldIds: string[];
  dependencyFieldIds: string[];
  allIds: string[];
};

type Dep = {
  if?: { properties?: Record<string, string> };
  then?: { required?: string[] | null; enabled?: string[] | null };
};

const uniq = (arr: string[]) => Array.from(new Set(arr));

const toIdStrings = (arr?: Array<string | number | null | undefined>) =>
  (arr ?? []).filter(Boolean).map((x) => String(x));

export const buildSectionIds = (
  schema?: GetQuestionnaireOutput | null,
): SectionIdsResult[] => {
  const sections = (schema?.data ?? []) as Array<{
    name?: string | null;
    properties?: Array<string | number>;
    dependencies?: Dep[] | null;
  }>;

  return sections.map((section, sectionIndex) => {
    const fieldIds = uniq(toIdStrings(section.properties ?? []));

    const deps = (section.dependencies ?? []) as Dep[];

    const dependencyFieldIds = uniq(
      toIdStrings(
        deps.flatMap((d) => [
          ...(d?.then?.enabled ?? []),
          ...(d?.then?.required ?? []),
        ]),
      ),
    );

    const allIds = uniq([...fieldIds, ...dependencyFieldIds]);

    return {
      sectionIndex,
      sectionName: section.name ?? null,
      fieldIds,
      dependencyFieldIds,
      allIds,
    };
  });
};

const formattedNdcRegex =
  /^(?:\d{4}-\d{4}-\d{2}|\d{5}-\d{3}-\d{2}|\d{5}-\d{4}-\d{1}|\d{5}-\d{4}-\d{2})$/;
const rawNdcRegex = /^(?:\d{10}|\d{11})$/;

export const isValidNdc = (input: string): boolean => {
  if (!input) return false;
  return (
    formattedNdcRegex.test(input) || rawNdcRegex.test(input.replace(/-/g, ''))
  );
};
